import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { QueryProvider } from "./providers/QueryProvider";
import { useAuthStore } from "./stores/authStore";
import useNotificationStore from "./stores/notificationStore";
import Navbar from "./componnents/Navbar/navbar";
import Shop from "./pages/Shop";
import Shopcategory from "./pages/Shopcategory";
import CategoryPage from "./pages/CategoryPage";
import Product from "./pages/Product";
import Cart from "./pages/Cart";
import Logine from "./pages/Login";
import "./App.css";
import "./utils/animations.css";
import Footer from "./componnents/Footer/Footer";
import kidban from "./componnents/Assets/kidban.jpg";
import womanban from "./componnents/Assets/womanban.jpg";
import manban from "./componnents/Assets/manban.jpg";
import SignUp from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Profile from "./pages/Profile";
import Checkout from "./pages/Checkout";
import SearchPage from "./pages/SearchPage";
import Boutiques from "./pages/Boutiques";
import BoutiqueDetail from "./pages/BoutiqueDetail";
import AddBidToCart from "./pages/AddBidToCart";
import ProductOffersRedirect from "./pages/ProductOffersRedirect";
import ConversationDeepLink from "./pages/ConversationDeepLink";
import Conversations from "./pages/Conversations";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import About from "./pages/About";
import HowTo from "./pages/HowTo";
import Feedback from "./pages/Feedback";
import ToastContainer from "./componnents/UI/ToastContainer";
import ScrollToTop from "./componnents/ScrollToTop/ScrollToTop";
import ErrorBoundary from "./componnents/ErrorBoundary/ErrorBoundary";
import LanguageInitializer from "./componnents/LanguageInitializer/LanguageInitializer";
import NavbarHeightCalculator from "./componnents/NavbarHeightCalculator/NavbarHeightCalculator";
import InactivityWatcher from "./componnents/InactivityWatcher/InactivityWatcher";
import { useAuth } from "./hooks/useAuth";
import SplashScreen from "./componnents/SplashScreen/SplashScreen";
import NotFound from "./pages/NotFound";
import OrderDeepLink from "./pages/OrderDeepLink";
// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  const [authReady, setAuthReady] = useState(() => useAuthStore.persist.hasHydrated());

  useEffect(() => {
    // Loop fix: wait for persisted auth before redirecting. Without this, isAuthenticated
    // is false for one frame, we Navigate to /login, then hydration sets true and bounce back.
    if (useAuthStore.persist.hasHydrated()) {
      setAuthReady(true);
      return undefined;
    }
    return useAuthStore.persist.onFinishHydration(() => setAuthReady(true));
  }, []);

  if (!authReady) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Guest Route Component (redirect if already logged in)
const GuestRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  const [authReady, setAuthReady] = useState(() => useAuthStore.persist.hasHydrated());

  useEffect(() => {
    if (useAuthStore.persist.hasHydrated()) {
      setAuthReady(true);
      return undefined;
    }
    return useAuthStore.persist.onFinishHydration(() => setAuthReady(true));
  }, []);

  if (!authReady) {
    return null;
  }

  if (isAuthenticated) {
    const from = location.state?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  return children;
};

// Layout component that conditionally renders navbar and footer
const Layout = ({ children }) => {
  const location = useLocation();
  const path = location.pathname.toLowerCase();

  // Auth pages where navbar and footer should be hidden
  const authPages = ["/login", "/signup", "/forgot-password", "/reset-password", "/verify-email"];
  const isAuthPage = authPages.includes(path);

  const infoPages = ["/privacy", "/about", "/how-to", "/feedback"];
  const isInfoPage = infoPages.includes(path);

  useEffect(() => {
    if (!isInfoPage) return undefined;
    document.body.classList.add("info-page");
    return () => document.body.classList.remove("info-page");
  }, [isInfoPage]);

  return (
    <>
      {!isAuthPage && !isInfoPage && <Navbar />}
      <main className={isAuthPage ? "auth-page" : isInfoPage ? "info-page" : ""}>{children}</main>
      {!isAuthPage && <Footer />}
    </>
  );
};

function AppRoutes() {
  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Shop />} />
        <Route
          path="/Hommes"
          element={<Shopcategory banner={manban} category="men" />}
        />
        <Route
          path="/Femmes"
          element={<Shopcategory banner={womanban} category="women" />}
        />
        <Route
          path="/Enfants"
          element={<Shopcategory banner={kidban} category="Kids" />}
        />
        
        {/* Dynamic Category Page */}
        <Route path="/category/:categoryId" element={<CategoryPage />} />

        {/* Boutiques (shop discovery) */}
        <Route path="/boutiques" element={<Boutiques />} />
        <Route path="/boutiques/:boutiqueId" element={<BoutiqueDetail />} />
        
        {/* Search Page with Filters */}
        <Route path="/search" element={<SearchPage />} />
        <Route path="/products" element={<SearchPage />} />
        
        {/* Info & legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/about" element={<About />} />
        <Route path="/how-to" element={<HowTo />} />
        <Route path="/feedback" element={<Feedback />} />
        
        {/* Product Page */}
        <Route path="/product/:productId" element={<Product />} />

        {/* Auth Routes (Guest Only) */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Logine />
            </GuestRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <GuestRoute>
              <SignUp />
            </GuestRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <ForgotPassword />
            </GuestRoute>
          }
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        <Route
          path="/verify-email"
          element={
            <GuestRoute>
              <VerifyEmail />
            </GuestRoute>
          }
        />

        {/* Protected Routes (Auth Required) */}
        <Route
          path="/carte"
          element={
            <ProtectedRoute>
              <Cart />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bid/:bidId/add-to-cart"
          element={
            <ProtectedRoute>
              <AddBidToCart />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product/:productId/offers"
          element={
            <ProtectedRoute>
              <ProductOffersRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/conversations"
          element={
            <ProtectedRoute>
              <Conversations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages/:conversationId"
          element={
            <ProtectedRoute>
              <ConversationDeepLink />
            </ProtectedRoute>
          }
        />
        
        {/* Wishlist - redirect to profile with wishlist tab */}
        <Route
          path="/wishlist"
          element={<Navigate to="/profile?tab=wishlist" replace />}
        />
        
        {/* Orders - redirect to profile with orders tab */}
        <Route
          path="/orders"
          element={<Navigate to="/profile?tab=orders" replace />}
        />
        <Route
          path="/orders/:orderId"
          element={
            <ProtectedRoute>
              <OrderDeepLink />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orderhistory"
          element={<Navigate to="/profile?tab=orders" replace />}
        />
        <Route
          path="/OrderHistory"
          element={<Navigate to="/profile?tab=orders" replace />}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

function ProfileSync() {
  useAuth();
  return null;
}

function App() {
  // Use selectors to prevent unnecessary re-renders
  // Use shallow equality to prevent re-renders when array reference changes but content is same
  const notifications = useNotificationStore((state) => state.notifications);
  const removeNotification = useNotificationStore((state) => state.removeNotification);
  const [showSplash, setShowSplash] = useState(true);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  return (
    <ErrorBoundary>
      <QueryProvider>
        <BrowserRouter>
          {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
          {!showSplash && (
            <>
              <LanguageInitializer />
              <NavbarHeightCalculator />
              <InactivityWatcher />
              <ProfileSync />
              <AppRoutes />
              <ToastContainer 
                notifications={notifications} 
                removeNotification={removeNotification} 
              />
              <ScrollToTop />
            </>
          )}
        </BrowserRouter>
      </QueryProvider>
    </ErrorBoundary>
  );
}

export default App;
